import type { DrawingDocument } from '../model';
import { FUNCTIONAL_GROUPS, functionalGroupDocument } from './functionalGroups.ts';
import { documentToRdKitInput } from './chemicalValidation.ts';
import { loadRdKit } from './rdkitAdapter.ts';
import { isChemicalBond } from '../domain/documentSelectors.ts';
import { replaceTopology } from '../domain/drawingDocumentCore.ts';
import { placeFunctionalGroupAt } from '../domain/functionalGroupPlacement.ts';
import {
  contractedGroupViews,
  hangingAtoms,
  withContractedGroups,
  withoutContractedGroups
} from '../domain/contractedGroups.ts';

/** The largest group's atom count, and some room: no end group bigger is looked at. */
const MOST_GROUP_ATOMS = 12;

/**
 * Each group's canonical SMILES with a stand-in on its bonding side -- deuterium, a one-bond atom
 * nothing else here is: a carbon stand-in made CO₂Me and OAc the same (both methyl acetate), as it
 * doesn't say which atom bonds.
 */
let canonicalGroups: Promise<Map<string, string>> | null = null;
function groupForms(): Promise<Map<string, string>> {
  canonicalGroups ??= loadRdKit().then((rdkit) => {
    const forms = new Map<string, string>();
    for (const group of FUNCTIONAL_GROUPS) {
      const molecule = rdkit.get_mol(`[2H]${group.smiles}`);
      if (molecule?.is_valid()) forms.set(molecule.get_smiles(), group.key);
      molecule?.delete();
    }
    return forms;
  });
  return canonicalGroups;
}

/** `document` cut down to `atomIds`, `standIn` made deuterium: an end group with its stand-in. */
function endGroupDocument(document: DrawingDocument, atomIds: ReadonlySet<string>, standIn: string): DrawingDocument {
  const { structure } = document;
  const keep = new Set([...atomIds, standIn]);
  const pick = <T>(record: Readonly<Record<string, T>>) =>
    Object.fromEntries(Object.entries(record).filter(([id]) => keep.has(id)));
  const edges = structure.graph.edges.filter((edge) => keep.has(edge.source) && keep.has(edge.target));
  const edgeIds = new Set(edges.map((edge) => edge.id));
  const cut = replaceTopology(
    document,
    { ...structure.graph, nodes: structure.graph.nodes.filter((node) => keep.has(node.id)), edges },
    { ...pick(structure.atoms), [standIn]: { atomicNumber: 1, isotope: 2 } },
    Object.fromEntries(Object.entries(structure.bonds).filter(([id]) => edgeIds.has(id))),
    { ...pick(structure.formalCharges), [standIn]: 0 },
    pick(document.depiction.coordinateSet.coordinates),
    {},
    {}
  );
  // No stereo or styles: a group is recognised by what it is, not how it is drawn.
  return {
    ...cut,
    structure: { ...cut.structure, stereochemistry: { tetrahedral: {}, doubleBonds: {} } },
    depiction: { ...cut.depiction, bondStyles: {} },
    annotations: [],
    derivedPropertySets: []
  };
}

/**
 * #736 step 2 (owner: any recognised group): the end groups among `atomIds` (all when absent) that
 * are exactly one of the groups -- however they were drawn, placed or imported -- drawn as their
 * labels from now on. An end group is what hangs off one bond, the smaller side of it (so a ring
 * scaffold is never "contracted" into the label of the methyl beside it); the largest match wins,
 * and none overlaps another.
 */
export async function contractRecognizedGroups(
  document: DrawingDocument,
  atomIds?: ReadonlySet<string>
): Promise<DrawingDocument> {
  const forms = await groupForms();
  const rdkit = await loadRdKit();
  const { structure } = document;
  const already = contractedGroupViews(document);
  const taken = new Set(already.flatMap((view) => [view.attachmentId, ...view.atomIds]));
  const componentSize = (start: string) => (hangingAtoms(document, start, undefined)?.size ?? 0) + 1;
  const candidates: { attachmentId: string; restId: string; atoms: Set<string>; key: string }[] = [];
  for (const edge of structure.graph.edges) {
    if (!isChemicalBond(structure.bonds[edge.id])) continue;
    for (const [restId, attachmentId] of [
      [edge.source, edge.target],
      [edge.target, edge.source]
    ] as const) {
      if (atomIds && !atomIds.has(attachmentId)) continue;
      const hanging = hangingAtoms(document, attachmentId, restId);
      if (!hanging || hanging.size === 0 || hanging.size + 1 > MOST_GROUP_ATOMS) continue;
      if (hanging.size + 1 >= componentSize(attachmentId) - (hanging.size + 1)) continue; // not the smaller side
      const atoms = new Set([attachmentId, ...hanging]);
      if ([...atoms].some((id) => taken.has(id))) continue;
      const molecule = rdkit.get_mol(documentToRdKitInput(endGroupDocument(document, atoms, restId)));
      const key = molecule?.is_valid() ? forms.get(molecule.get_smiles()) : undefined;
      molecule?.delete();
      if (key) candidates.push({ attachmentId, restId, atoms: hanging, key });
    }
  }
  candidates.sort((a, b) => b.atoms.size - a.atoms.size);
  const chosen: typeof candidates = [];
  for (const candidate of candidates) {
    const all = [candidate.attachmentId, ...candidate.atoms];
    if (all.some((id) => taken.has(id))) continue;
    all.forEach((id) => taken.add(id));
    chosen.push(candidate);
  }
  return withContractedGroups(
    document,
    chosen.map((candidate) => ({
      attachmentId: candidate.attachmentId,
      group: candidate.key,
      atomIds: [...candidate.atoms]
    }))
  );
}

/**
 * #736 step 2: the contracted groups among `atomIds` (all when absent) drawn as their atoms again --
 * laid out afresh from where the label is now, as a group is placed (so a moved label never leaves
 * its atoms behind). The atoms are new ones in the same arrangement; the molecule is unchanged.
 */
export async function expandContractedGroups(
  document: DrawingDocument,
  atomIds?: ReadonlySet<string>
): Promise<DrawingDocument> {
  let result = document;
  for (const view of contractedGroupViews(document)) {
    if (atomIds && !atomIds.has(view.attachmentId) && ![...view.atomIds].some((id) => atomIds.has(id))) continue;
    const group = await functionalGroupDocument(view.group);
    const { structure } = result;
    const nodes = structure.graph.nodes.filter((node) => !view.atomIds.has(node.id));
    const edges = structure.graph.edges.filter(
      (edge) => !view.atomIds.has(edge.source) && !view.atomIds.has(edge.target)
    );
    const nodeIds = new Set(nodes.map((node) => node.id));
    const edgeIds = new Set(edges.map((edge) => edge.id));
    const keep = <T>(record: Readonly<Record<string, T>>, ids: ReadonlySet<string>) =>
      Object.fromEntries(Object.entries(record).filter(([id]) => ids.has(id)));
    const bare = replaceTopology(
      withoutContractedGroups(result, new Set([view.attachmentId])),
      { ...structure.graph, revision: structure.graph.revision + 1, nodes, edges },
      keep(structure.atoms, nodeIds),
      keep(structure.bonds, edgeIds),
      keep(structure.formalCharges, nodeIds),
      keep(result.depiction.coordinateSet.coordinates, nodeIds),
      keep(result.depiction.atomLabels, nodeIds),
      keep(result.depiction.bondLengthOverrides, edgeIds)
    );
    result = placeFunctionalGroupAt(bare, group, { atomId: view.attachmentId }).document;
  }
  return result;
}
